import React, { useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import { Box, Button, Heading, useDisclosure } from '@chakra-ui/react';
import ReactPlayer from 'react-player';
import useConversationAreas from '../../hooks/useConversationAreas';
import useCoveyAppState from '../../hooks/useCoveyAppState';
import usePlayerMovement from '../../hooks/usePlayerMovement';
import { ServerPlayer } from '../../classes/Player';
import ConversationArea, { NO_TOPIC_STRING } from '../../classes/ConversationArea';
import VoteModal from '../world/VoteModal';
import CalledVote from './CalledVote';
import DictatorForm from './DictatorForm';
import usePlayersInTown from '../../hooks/usePlayersInTown'

type PlayerProps = {
  area: ConversationArea,
  socket: Socket | null,
  occupants: string[],
  player_id: string,
  media: string[],
};

function skipVideo({ area, socket, occupants, player_id }: PlayerProps) {
  if (socket && player_id === occupants[0]) {
    const areacopy = area.copy();
    areacopy.media.splice(0, 1);
    socket.emit('setMedia', areacopy.toServerConversationArea());
  }
}

export function MediaPlayer({ area, socket, occupants, player_id, media }: PlayerProps): JSX.Element {
  return (<ReactPlayer
    url={media[0]}
    controls={false}
    playing
    config={{
      youtube: {
        playerVars: { disablekb: 1 }
      }
    }}
    onEnded={() => skipVideo({ area, socket, occupants, player_id, media })}
    onError={() => skipVideo({ area, socket, occupants, player_id, media })}
  />);
}

type ActiveConversationHubProps = {
  area: ConversationArea;
};

function ActiveConversationHub({ area }: ActiveConversationHubProps): JSX.Element {
  const players = usePlayersInTown();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { socket } = useCoveyAppState();

  // State for this component is the list of occupants
  const [convoOccupants, setState] = useState(area.occupants);

  const [proposedMedia, setProposedMedia] = useState<string | undefined>(area.proposedMedia);
  const [media, setMedia] = useState<string[]>(area.media);


  // This should only be called when component mounts (and the cleaner when it unmounts)
  // the subscribed listener should take care of updating the state
  useEffect(() => {
    const updateComponentState = {
      onOccupantsChange: (newOccupants: string[]) => {
        setState(newOccupants);
      },
    };
    area.addListener(updateComponentState);

    return () => area.removeListener(updateComponentState);
  }, [area]);

  useEffect(() => {
    const updateMediaState = {
      onMediaChange: (newMedia: string[]) => {
        setMedia(newMedia);
      },
    };
    area.addListener(updateMediaState);

    return () => area.removeListener(updateMediaState);
  }, [media, area]);

  useEffect(() => {
    const updateProposedMediaState = {
      onProposedMediaChange: (newProposedMedia: string | undefined) => {
        setProposedMedia(newProposedMedia);
      },
    };
    area.addListener(updateProposedMediaState);

    return () => area.removeListener(updateProposedMediaState);
  }, [area]);

  return (
    <Box>
      <Heading fontSize='md' >You are currently in active conversation area: {area.topic}</Heading>

      {media.length > 0 ?
        <h3>Current Media: {media[0]} </h3> :
        <h3>No Current Media</h3>
      }

      {media.length > 1 ?
        <h3>Queue: {media.slice(1).map((m, index) => <li key={index.toString()}>{m} </li>)}</h3> :
        <div />}

      {area.isDemocratic ?
        <div>
          {!proposedMedia ? <Button onClick={onOpen}>Call Vote</Button> : <></>}
          <VoteModal isOpen={isOpen} onClose={onClose} curConversation={area} />
          {proposedMedia ? <CalledVote conversation={area} newMedia={proposedMedia} /> : <></>}
          {media.length > 0 ? <MediaPlayer area={area} socket={socket}
            occupants={convoOccupants} player_id={convoOccupants[0]} media={media} /> : <></>}
        </div> :
        <div>
          <h3>DJ: {players.find(p => p.id === convoOccupants[0])?.userName}</h3>
          <DictatorForm key={convoOccupants[0]} conversation={area} leaderId={convoOccupants[0]} />
          {media.length > 0 ? <MediaPlayer area={area} socket={socket}
            occupants={convoOccupants} player_id={convoOccupants[0]} media={media} /> : <></>}
        </div>
      }
    </Box>
  );
};

export default function CurrentConversationDisplay(): JSX.Element {
  const coveyState = useCoveyAppState();
  const { myPlayerID } = coveyState;
  const conversationAreas = useConversationAreas();

  function findCurrentArea(): ConversationArea | undefined {
    return conversationAreas.find(eachArea => eachArea.topic !== NO_TOPIC_STRING && eachArea.occupants.includes(myPlayerID));
  };

  const [state, setState] = useState<ConversationArea | undefined>(findCurrentArea());
  const playerMovementCallbacks = usePlayerMovement();

  useEffect(() => {
    const movementDispatcher = (player: ServerPlayer) => {
      if (player._id === myPlayerID) {
        setState(findCurrentArea());
      }
    };
    playerMovementCallbacks.push(movementDispatcher);
    return () => {
      playerMovementCallbacks.splice(playerMovementCallbacks.indexOf(movementDispatcher), 1);
    };
  });

  if (state) {
    return (
      <Box>
        <Heading as='h2' fontSize='l'>Current Area Hub:</Heading>
        <ActiveConversationHub key={state.label} area={state} />
      </Box>
    );
  }
  return (
    <Box>
      <Heading as='h2' fontSize='l'>Current Area Hub:</Heading>
      <h3>Not in an active area</h3>
    </Box>
  );
}

